import {
  AfterViewInit,
  ChangeDetectorRef,
  Directive,
  ElementRef,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
} from "@angular/core";

@Directive({
  selector: "[homepageInlineText]",
  exportAs: "homepageInlineText",
})
export class HomepageInlineTextDirective
  implements AfterViewInit, OnChanges, OnDestroy
{
  @Input() homepageInlineText: boolean = false;
  public hasText: boolean = false;
  private viewInitialized: boolean = false;
  private active: boolean = false;
  private frame: number | null = null;
  private observer: MutationObserver;
  private messageRoot: HTMLElement | null = null;
  private view: Window & typeof globalThis;
  private renderEvents = [
    "toggle",
    "animationstart",
    "animationend",
    "animationcancel",
    "transitionrun",
    "transitionend",
    "transitioncancel",
  ];

  constructor(
    private element: ElementRef<HTMLElement>,
    private zone: NgZone,
    private changeDetector: ChangeDetectorRef
  ) {
    this.view = this.element.nativeElement.ownerDocument.defaultView;
  }

  ngAfterViewInit(): void {
    this.viewInitialized = true;
    this.updateObservation();
  }

  ngOnChanges(): void {
    this.updateObservation();
  }

  ngOnDestroy(): void {
    this.stopObservation();
  }

  private updateObservation(): void {
    if (!this.homepageInlineText) {
      this.stopObservation();
      this.setHasText(false);
      return;
    }
    if (!this.viewInitialized || this.active || !this.view) {
      return;
    }

    this.active = true;
    this.zone.runOutsideAngular(() => {
      const host = this.element.nativeElement;
      this.messageRoot = this.findMessageRoot();
      this.observer = new this.view.MutationObserver((records) => {
        const root = this.findMessageRoot();
        const rootChanged = root !== this.messageRoot;
        this.messageRoot = root;
        // Ignore logo spacer classes and greeting updates beside the messages.
        if (
          rootChanged ||
          records.some((record) => this.affectsMessages(record.target))
        ) {
          this.scheduleRead();
        }
      });
      this.observer.observe(host, {
        subtree: true,
        childList: true,
        characterData: true,
        attributes: true,
      });
      // Ionic caches pages and reveals them by changing ancestor classes.
      for (
        let ancestor = host.parentElement;
        ancestor;
        ancestor = ancestor.parentElement
      ) {
        this.observer.observe(ancestor, {
          attributes: true,
          attributeFilter: ["class", "style", "hidden"],
        });
      }
      this.view.addEventListener("resize", this.scheduleRead);
      this.view.addEventListener("orientationchange", this.scheduleRead);
      this.renderEvents.forEach((event) =>
        this.view.addEventListener(event, this.onRenderEvent, true)
      );
      this.scheduleRead();
    });
  }

  private stopObservation(): void {
    this.active = false;
    this.observer?.disconnect();
    this.observer = null;
    if (this.view) {
      this.view.removeEventListener("resize", this.scheduleRead);
      this.view.removeEventListener("orientationchange", this.scheduleRead);
      this.renderEvents.forEach((event) =>
        this.view.removeEventListener(event, this.onRenderEvent, true)
      );
      if (this.frame !== null) {
        this.view.cancelAnimationFrame(this.frame);
      }
    }
    this.frame = null;
    this.messageRoot = null;
  }

  private findMessageRoot(): HTMLElement | null {
    return this.element.nativeElement.querySelector(
      "[data-homepage-inline-messages]"
    );
  }

  private affectsMessages(target: Node): boolean {
    return !!(
      this.messageRoot &&
      (this.messageRoot.contains(target) || target.contains(this.messageRoot))
    );
  }

  private onRenderEvent = (event: Event): void => {
    if (
      event.target instanceof this.view.Node &&
      this.affectsMessages(event.target)
    ) {
      this.scheduleRead();
    }
  };

  private scheduleRead = (): void => {
    if (!this.active || this.frame !== null) {
      return;
    }
    this.frame = this.view.requestAnimationFrame(() => {
      this.frame = null;
      if (this.active) {
        this.setHasText(this.containsRenderedText());
      }
    });
  };

  private setHasText(hasText: boolean): void {
    if (this.hasText !== hasText) {
      this.zone.run(() => {
        this.hasText = hasText;
        this.changeDetector.markForCheck();
      });
    }
  }

  private containsRenderedText(): boolean {
    const root = this.messageRoot;
    if (!root?.isConnected) {
      return false;
    }
    // innerText falls back to textContent for a non-rendered root.
    for (
      let ancestor: Element = root;
      ancestor;
      ancestor = ancestor.parentElement
    ) {
      if (this.hidesSubtree(this.view.getComputedStyle(ancestor))) {
        return false;
      }
    }
    if (!this.isNonblank(root.innerText)) {
      return false;
    }
    return this.elementContainsText(root);
  }

  private elementContainsText(element: Element): boolean {
    const style = this.view.getComputedStyle(element);
    if (
      this.hidesSubtree(style) ||
      ["AUDIO", "VIDEO", "SCRIPT", "STYLE", "TEMPLATE"].includes(
        element.tagName
      )
    ) {
      return false;
    }
    if (element.tagName === "DETAILS" && !element.hasAttribute("open")) {
      const summary = Array.from(element.children).find(
        (child) => child.tagName === "SUMMARY"
      );
      return !!summary && this.elementContainsText(summary);
    }

    return Array.from(element.childNodes).some((node) => {
      if (node.nodeType === this.view.Node.ELEMENT_NODE) {
        // visibility can be restored on a descendant of a hidden element.
        return this.elementContainsText(node as Element);
      }
      if (
        node.nodeType !== this.view.Node.TEXT_NODE ||
        style.visibility !== "visible" ||
        !this.isNonblank(node.textContent)
      ) {
        return false;
      }
      const range = element.ownerDocument.createRange();
      range.selectNodeContents(node);
      // Check that text has a rendered box, without treating offscreen or
      // clipped text as absent: the margin fix can itself uncover that text.
      return Array.from(range.getClientRects()).some(
        (rect) => rect.width > 0 && rect.height > 0
      );
    });
  }

  private hidesSubtree(style: CSSStyleDeclaration): boolean {
    return (
      style.display === "none" ||
      style.opacity === "0" ||
      style.contentVisibility === "hidden"
    );
  }

  private isNonblank(text: string | null | undefined): boolean {
    return !!text?.replace(/[\u200B-\u200D\u2060\uFEFF]/g, "").trim();
  }
}
